import { GET, DELETE } from '@/utils/api/endpoints'
import i18n from '@/utils/plugins/i18n'

/**
 * @param {() => Record<string, any>} initialState
 * @returns {(options?: { preserveKeys?: string[] } | string) => void}
 */
export function makeResetStoreAction (initialState) {
  /**
   * @param {string | { preserveKeys?: string[] }} options - Legacy: single key to reset, or options object with preserveKeys array
   * @returns {void}
   */
  return function (options) {
    if (typeof options === 'string') {
      this.$state[options] = initialState()[options]
      return
    }

    if (!options || !options.preserveKeys || options.preserveKeys.length === 0) {
      this.$state = initialState()
      return
    }

    const initialStateValue = initialState()
    Object.keys(initialStateValue).forEach((key) => {
      if (!options.preserveKeys.includes(key)) {
        this.$state[key] = initialStateValue[key]
      }
    })
  }
}

/**
 * Creates an action to set errors in a Pinia store
 * @returns {function({ errors?: Array, key?: string }): void} Function that sets errors
 * @example
 * // Set all errors
 * setErrors({ errors: ['error1', 'error2'] })
 * // Set errors for specific key
 * setErrors({ errors: ['error1'], key: 'fieldName' })
 */
export function makeSetErrorsAction () {
  return function ({ errors = [], key }) {
    if (!_.isPlainObject(arguments[0])) return console.error('[SET_ERRORS] must be passed an Object', arguments[0])
    if (_.isEmpty(errors)) console.warn(`[SET_ERRORS] passed empty errors, call CLEAR_ERRORS to clear errors instead.`)
    if (key) {
      this.$state.errors[key] = errors
    } else {
      this.$state.errors = errors
    }
  }
}

/**
 * Creates an action to clear errors in a Pinia store
 * @param {() => Record<string, any>} state - Function that returns the initial state
 * @returns {(key?: string) => void} Function that clears errors
 * @example
 * // Clear all errors
 * clearErrors()
 * // Clear errors for specific key
 * clearErrors('fieldName')
 */
export function makeClearErrorsAction (state) {
  return function (key) {
    if (key) this.$state.errors[key] = state().errors[key]
    else this.$state.errors = state().errors
  }
}

/**
 * Creates an action to get owner object with full user details
 * @param {Function} useOrganizationStore - The organization store composable
 * @returns {function({ data: any, oldVal: any }): Promise<any>} Function that returns object with populated owner
 * @example
 * // Get owner object with full details
 * const result = await getOwnerObject({ data: { owner: 'username' }, oldVal: {} })
 */
export function makeGetOwnerObject () {
  return async function ({ data: newVal, oldVal }) {
    const { useOrganizationStore } = await import('@/store/pinia/organization')
    if (_.has(newVal.owner, 'username')) return newVal

    const username = newVal.owner
    const oldUsername = oldVal.owner?.username ?? oldVal.owner

    if (username === oldUsername && _.has(oldVal.owner, 'username')) {
      return _.merge(newVal, { owner: oldVal.owner })
    }

    const organizationStore = useOrganizationStore()
    await organizationStore.FETCH_AVAILABLE({ keyPath: 'members' })

    const fullOwnerObject = _.find(organizationStore.available.members, ['username', username])
    const backupOwnerObject = { username }

    return _.merge(newVal, { owner: fullOwnerObject || backupOwnerObject })
  }
}

export function makeStartFetchAction () {
  return function (key) {
    this.$state.fetchInProgress[key] = true
  }
}

export function makeStopFetchAction () {
  return function (key) {
    this.$state.fetchInProgress[key] = false
  }
}

export function makeInFlightRequestDeduper () {
  const inFlightRequests = new Map()

  return async function (requestKey, requestCallback) {
    const inFlightRequest = inFlightRequests.get(requestKey)

    if (inFlightRequest) {
      return await inFlightRequest
    }

    const request = (async () => {
      return await requestCallback()
    })()
    inFlightRequests.set(requestKey, request)

    try {
      return await request
    } finally {
      inFlightRequests.delete(requestKey)
    }
  }
}

// Key mapping for consistent naming
export const keyMap = /** @type {const} */ ({
  config_repositories: 'configRepositories',
  service_catalog_sources: 'catalogRepositories',
  serviceCatalogSources: 'catalogRepositories',
  service_catalogs: 'stacks',
  serviceCatalogs: 'stacks',
  users: 'members',
})

/**
 * We aim to have the same naming everywhere...
 * but until then, these names reflect our UI naming, to make things simpler to understand
 *
 * Get the preferred key name for consistent naming
 * @template {keyof typeof keyMap | (string & {})} T
 * @param {T} key - The key to normalize
 * @returns {T extends keyof typeof keyMap ? typeof keyMap[T] : T} The preferred key name
 */
export function getKey (key) {
  return keyMap[key] || key
}

/**
 * Validates bulk delete parameters
 * @param {string} keyPath - The API endpoint path
 * @param {Array} toDelete - Items to delete
 * @returns {Object|null} Validation result or null if valid
 */
function validateBulkDeleteParams (keyPath, toDelete) {
  if (_.some([keyPath, toDelete], _.isEmpty)) {
    return { error: `[BULK_DELETE]: you must define the params keyPath and toDelete`, data: { keyPath, toDelete } }
  }
  return null
}

/**
 * Validates API configuration for bulk delete
 * @param {string} keyPreferred - The preferred key name
 * @param {string} keyPath - The API endpoint path
 * @param {Function} apiCall - The API call function
 * @returns {Object|null} Validation result or null if valid
 */
function validateBulkDeleteConfig (keyPreferred, keyPath, apiCall) {
  const isFetchingOrgs = keyPreferred === 'organizations'

  if (isFetchingOrgs) {
    return { error: `[BULK_DELETE]: you cannot use this action to delete multiple organizations, instead it is used for entities within an org scope` }
  }

  if (!apiCall) {
    return { error: `[BULK_DELETE]: key passed does not have a matching endpoint listed in utils/config/endpoints.js`, data: { keyPath } }
  }

  return null
}

/**
 * Clears errors for the given key
 * @param {Object} context - The store context
 * @param {string} keyPreferred - The preferred key name
 */
function clearBulkDeleteErrors (context, keyPreferred) {
  if (context.CLEAR_ERRORS) {
    context.CLEAR_ERRORS(keyPreferred)
  } else if (context.$state.errors) {
    if (keyPreferred) {
      context.$state.errors[keyPreferred] = []
    } else {
      context.$state.errors = []
    }
  }
}

/**
 * Handles successful deletions
 * @param {Array} successes - Successfully deleted items
 * @param {Object} $router - Vue router instance
 * @param {string} redirectTo - Redirect destination
 * @param {Object} alertStore - Alerts store instance
 */
async function handleBulkDeleteSuccesses (successes, $router, redirectTo) {
  if (_.isEmpty(successes)) return
  const { useAlertsStore } = await import('@/store/pinia/alerts')
  const alertStore = useAlertsStore()

  if ($router) $router.push({ name: redirectTo })
  const deleted = _.$getListFromArray(successes, { boldItems: true })
  const content = i18n.global.t('alerts.success.bulkDeleted', successes.length, { named: { deleted } })
  alertStore.SHOW_ALERT({ type: 'success', content })
}

/**
 * Handles deletion errors
 * @param {Array} errors - Deletion errors
 * @param {Object} context - The store context
 * @param {string} keyPreferred - The preferred key name
 */
function handleBulkDeleteErrors (errors, context, keyPreferred) {
  if (_.isEmpty(errors)) return

  const flattenedErrors = _.flatten(errors)

  if (context.SET_ERRORS) {
    context.SET_ERRORS({ key: keyPreferred, errors: flattenedErrors })
  } else if (context.$state.errors) {
    if (keyPreferred) {
      context.$state.errors[keyPreferred] = flattenedErrors
    } else {
      context.$state.errors = flattenedErrors
    }
  }
}

/**
 * Creates a bulk delete action for Pinia stores
 * @returns {(params: { keyPath: string, toDelete: Array<{canonical?: string, id?: string, name?: string, email?: string}>, $router?: any, redirectTo?: string, extraParams?: any[], publicEndpoint?: boolean, apiCallOptions?: { ignore_config_files_err?: boolean } }) => Promise<void>}
 * @example
 * // In your Pinia store
 * const actions = {
 *   BULK_DELETE: makeBulkDeleteAction(),
 *   // ... other actions
 * }
 */
export function makeBulkDeleteAction () {
  return async function ({ keyPath, toDelete, $router, redirectTo = keyPath && getKey(((keyPath).split('.')).at(-1)), extraParams = [], publicEndpoint = false, apiCallOptions }) {
    const paramValidation = validateBulkDeleteParams(keyPath, toDelete)
    if (paramValidation) return console.error(paramValidation.error, paramValidation.data)

    const key = (keyPath.split('.')).at(-1)
    const keyPreferred = getKey(key)
    const isKeyPathOK = keyPreferred === key
    const apiCall = _.get(DELETE, keyPath)

    // Validate configuration
    const configValidation = validateBulkDeleteConfig(keyPreferred, keyPath, apiCall)
    if (configValidation) return console.error(configValidation.error, configValidation.data)

    if (!isKeyPathOK) console.warn(`[BULK_DELETE]: please pass the preferred key, as used on the state`, { keyPath, keyPreferred })

    const { useOrganizationStore } = await import('@/store/pinia/organization')
    const orgStore = useOrganizationStore()

    // Clear errors
    clearBulkDeleteErrors(this, keyPreferred)

    const promises = toDelete.map(async ({ canonical, id, name, email }) => {
      const { errors } = (await this.$cycloid.ydAPI[apiCall](...(publicEndpoint ? [] : [orgStore.orgCanonical]), ...extraParams, canonical || id, ...(apiCallOptions !== undefined ? [apiCallOptions] : []))) || {}
      if (errors) errors[0].message = `<b><code>${name || canonical || email}</code></b> ${errors[0].message}`
      return errors || name || canonical || email
    })
    const { true: successes, false: errors } = _(await Promise.allSettled(promises)).map('value').groupBy(_.isString).value()

    await handleBulkDeleteSuccesses(successes, $router, redirectTo)
    handleBulkDeleteErrors(errors, this, keyPreferred)
  }
}

/**
 * Validates fetch available parameters
 * @param {string} keyPath - The API endpoint path
 * @param {Object} state - The store state
 * @returns {Object|null} Validation result or null if valid
 */
function validateFetchAvailableParams (keyPath, state) {
  if (_.isEmpty(keyPath)) {
    return { error: `[FETCH_AVAILABLE]: you must define the keyPath param`, data: { keyPath } }
  }
  if (!_.has(state, 'available')) {
    return { error: `[FETCH_AVAILABLE]: state must contain the .available property, but it was not found.` }
  }
  return null
}

/**
 * Validates API configuration for fetch available
 * @param {string} keyPath - The API endpoint path
 * @param {Function} apiCall - The API call function
 * @param {boolean} isFetchingOrgs - Whether fetching organizations
 * @returns {Object|null} Validation result or null if valid
 */
function validateFetchAvailableConfig (keyPath, apiCall, isFetchingOrgs) {
  if (!apiCall && !isFetchingOrgs) {
    return { error: `[FETCH_AVAILABLE]: GET key was not found in api/endpoints.js`, data: { keyPath } }
  }
  return null
}

/**
 * Clears errors for fetch available if needed
 * @param {Object} context - The store context
 * @param {string} keyPreferred - The preferred key name
 * @param {boolean} clearErrors - Whether to clear errors
 */
function clearFetchAvailableErrors (context, keyPreferred, clearErrors) {
  if (!clearErrors) return

  if (context.CLEAR_ERRORS) {
    context.CLEAR_ERRORS(keyPreferred)
  } else if (context.$state.errors) {
    if (keyPreferred) {
      context.$state.errors[keyPreferred] = []
    } else {
      context.$state.errors = []
    }
  }
}

/**
 * Starts fetch progress indicator
 * @param {Object} context - The store context
 * @param {string} keyPreferred - The preferred key name
 */
function startFetchProgress (context, keyPreferred) {
  if (context.START_FETCH) {
    context.START_FETCH(keyPreferred)
  } else if (context.$state.fetchInProgress) {
    context.$state.fetchInProgress[keyPreferred] = true
  }
}

/**
 * Stops fetch progress indicator
 * @param {Object} context - The store context
 * @param {string} keyPreferred - The preferred key name
 */
function stopFetchProgress (context, keyPreferred) {
  if (context.STOP_FETCH) {
    context.STOP_FETCH(keyPreferred)
  } else if (context.$state.fetchInProgress) {
    context.$state.fetchInProgress[keyPreferred] = false
  }
}

/**
 * Handles API response data for fetch available
 * @param {Object} context - The store context
 * @param {string} keyPreferred - The preferred key name
 * @param {any} data - The response data
 * @param {any} filters - The response filters
 * @param {Array} errors - The response errors
 * @param {boolean} updateFilters - Whether to update filters
 */
function handleFetchAvailableResponse (context, keyPreferred, data, filters, errors, updateFilters) {
  if (data) {
    context.SET_AVAILABLE({ key: keyPreferred, value: data })
  }

  if (updateFilters && filters) {
    context.SET_FILTERS({ key: keyPreferred, value: filters })
  }

  if (errors) {
    context.SET_ERRORS({ key: keyPreferred, errors })
  }
}

/**
 * Creates a fetch available action for Pinia stores
 * @returns {(params: { keyPath: string, extraParams?: any[], clearErrors?: boolean, updateFilters?: boolean }) => Promise<void>} Fetch available action function
 * @example
 * // In your Pinia store
 * const actions = {
 *   FETCH_AVAILABLE: makeFetchAvailableAction(),
 *   // ... other actions
 * }
 */
export function makeFetchAvailableAction () {
  return async function ({ keyPath, extraParams = [], clearErrors = true, updateFilters = false }) {
    const paramValidation = validateFetchAvailableParams(keyPath, this.$state)
    if (paramValidation) return console.error(paramValidation.error, paramValidation.data)

    const key = (keyPath.split('.')).at(-1)
    const keyPreferred = getKey(key)
    const isKeyPathOK = keyPreferred === key
    const apiCall = _.get(GET, keyPath)
    const isFetchingOrgs = keyPreferred === 'organizations'

    // Validate configuration
    const configValidation = validateFetchAvailableConfig(keyPath, apiCall, isFetchingOrgs)
    if (configValidation) return console.error(configValidation.error, configValidation.data)

    if (!isKeyPathOK) console.warn(`[FETCH_AVAILABLE]: please change to use the preferred key, as used on the state`, { keyPath, keyPreferred })

    // Get organization store for orgCanonical and FETCH_ORGS
    const { useOrganizationStore } = await import('@/store/pinia/organization')
    const orgStore = useOrganizationStore()

    clearFetchAvailableErrors(this, keyPreferred, clearErrors)
    startFetchProgress(this, keyPreferred)

    const { useRootStore } = await import('@/store/pinia/root')
    const rootStore = useRootStore()
    if (isFetchingOrgs) {
      await rootStore.FETCH_ORGS()
    } else {
      const { data, errors, filters } = (await this.$cycloid.ydAPI[apiCall](orgStore.orgCanonical, ...extraParams)) || {}
      handleFetchAvailableResponse(this, keyPreferred, data, filters, errors, updateFilters)
    }

    stopFetchProgress(this, keyPreferred)
  }
}

export const VERSION_KEY = 'version'

/**
 * Creates a persisted state configuration with built-in versioning support.
 *
 * @param {string} key - The key to store the state under in local storage.
 * @param {object} options - Configuration options.
 * @param {string[]} options.includePaths - Paths to include in persistence.
 * @param {number} options.version - The current version of the store state.
 * @param {Record<number, (state: object) => object>} [options.migrations] - Map of version numbers to migration functions.
 * @param {Storage} [options.storage] - Storage backend to persist to (defaults to the plugin's localStorage). Pass `sessionStorage` for per-tab isolation.
 * @returns {object} The persisted state configuration object.
 */
export function createPersistedState (key, { includePaths, version, migrations = {}, storage } = {}) {
  if (!version) {
    console.error(`[Pinia] Version is required for ${key} store`)
    return {}
  }
  let paths = includePaths
  if (Array.isArray(includePaths)) {
    paths = includePaths.includes(VERSION_KEY) ? includePaths : [...includePaths, VERSION_KEY]
  }

  return {
    persist: true,
    key,
    includePaths: paths,
    ...(storage ? { storage } : {}),
    migrate: (state) => {
      let currentState = { ...state }
      const storeVersion = currentState[VERSION_KEY] || 0

      const migrationVersions = Object.keys(migrations)
        .map(Number)
        .toSorted((a, b) => a - b)

      for (const v of migrationVersions) {
        if (v > storeVersion && v <= version) {
          try {
            if (migrations[v]) {
              currentState = migrations[v](currentState)
            }
          } catch (error) {
            console.error(`[Pinia] Migration failed for version ${v}`, error)
          }
        }
      }

      return {
        ...currentState,
        [VERSION_KEY]: version,
      }
    },
  }
}
